import { useState } from "react";
import { api } from "../../../api";
import { ErrorNote } from "../../../components/Feedback";
import { Btn, Field, G_DARK, Input, Select } from "../../../components/Shared";
import DatasetPicker from "./DatasetPicker";
import { FILE_ROLES, parseExtensions } from "./terms";

// File-name patterns of a table that carries no file-name column: "{run_id}_{sample_id}.fastq.gz"
// names one file per row, built from the row's columns. The build adds the expanded name as a
// text column and links it like a file column (backend services/file_refs.py). "Datasets to look
// in" (DatasetPicker) is the pattern's scope; none chosen: this dataset.
//
//   file.file_patterns                       [{column, pattern, role, extensions, scope}]
//   PUT /api/files/{id}/file-patterns        {patterns: [...]} -> the file; 422 names an unknown
//                                            placeholder column

const mono = { fontFamily: "'JetBrains Mono', monospace", fontSize: 13 };

export default function FilePatterns({ dataset, file, columns, canEdit, reloadFiles }) {
  const patterns = file.file_patterns || [];
  const [adding, setAdding] = useState(false);
  const [column, setColumn] = useState("");
  const [pattern, setPattern] = useState("");
  const [role, setRole] = useState("");
  const [extensions, setExtensions] = useState("");
  const [scope, setScope] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");

  if (!canEdit && patterns.length === 0) return null;

  const put = async (next, message) => {
    setBusy(true); setError(null); setNotice("");
    try {
      await api.put(`/files/${file.id}/file-patterns`, { patterns: next });
      setNotice(message);
      setAdding(false); setColumn(""); setPattern(""); setRole(""); setExtensions(""); setScope([]);
      reloadFiles();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const add = e => {
    e.preventDefault();
    const p = { column: column.trim(), pattern: pattern.trim(), role: role || null,
                extensions: parseExtensions(extensions), scope };
    put([...patterns, p], `Saved the pattern ${p.pattern}.`);
  };

  return (
    <section aria-label="File-name patterns" style={{ marginTop: 18 }}>
      <h4 style={{ margin: "0 0 6px", fontSize: 14 }}>File-name patterns</h4>
      <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 10px", maxWidth: 760 }}>
        No column with the file name? Build it from the columns, e.g.{" "}
        <span style={mono}>{"{run_id}_{sample_id}.fastq.gz"}</span>. Each row is then linked to that file
        of this dataset, or of the datasets chosen to look in; a name that matches no file blocks the build.
      </p>
      <div role="status" aria-live="polite" style={{ fontSize: 13, color: G_DARK }}>{notice}</div>
      <ErrorNote error={error} />
      {patterns.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 10px", display: "flex", flexDirection: "column", gap: 6 }}>
          {patterns.map(p => (
            <li key={p.column} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 14 }}>
              <span style={mono}>{p.column}</span> = <span style={mono}>{p.pattern}</span>
              {p.role && <span style={{ color: "#4b5563" }}>({p.role})</span>}
              {(p.scope || []).length > 0 && (
                <span style={{ color: "#4b5563" }}>
                  looks in {p.scope.length} dataset{p.scope.length === 1 ? "" : "s"}
                </span>
              )}
              {canEdit && (
                <Btn variant="ghost" disabled={busy} style={{ padding: "4px 10px" }}
                     onClick={() => put(patterns.filter(x => x.column !== p.column), `Removed the pattern ${p.pattern}.`)}>
                  <span aria-hidden="true">Remove</span>
                  <span style={{ position: "absolute", left: -9999 }}>Remove the pattern for {p.column}</span>
                </Btn>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && !adding && <Btn variant="secondary" onClick={() => setAdding(true)}>Add a file-name pattern</Btn>}
      {canEdit && adding && (
        <form onSubmit={add} aria-label="New file-name pattern"
              style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, alignItems: "end" }}>
          <Field label="Column name" hint="The built column holding the name, e.g. fastq_file">
            <Input value={column} onChange={e => setColumn(e.target.value)} required />
          </Field>
          <Field label="Pattern" hint={`Placeholders: ${columns.map(c => `{${c.name}}`).slice(0, 4).join(" ")}`}>
            <Input value={pattern} onChange={e => setPattern(e.target.value)} required />
          </Field>
          <Field label="Role" hint="Only files of this role match (empty: any)">
            <Select value={role} onChange={e => setRole(e.target.value)} options={FILE_ROLES} />
          </Field>
          <Field label="Extensions" hint="Comma-separated (empty: any)">
            <Input value={extensions} onChange={e => setExtensions(e.target.value)} />
          </Field>
          {dataset && (
            <div style={{ gridColumn: "1 / -1" }}>
              <DatasetPicker projectId={dataset.project_id} currentId={dataset.id} value={scope} onChange={setScope} />
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn type="submit" disabled={busy}>{busy ? "Saving…" : "Save pattern"}</Btn>
            <Btn variant="secondary" onClick={() => setAdding(false)} disabled={busy}>Cancel</Btn>
          </div>
        </form>
      )}
    </section>
  );
}
